import { useMemo } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { calculateTierProgressionEligibility } from 'loyalty/lib/calculateTierProgressionEligibility'
import getLoyaltyTierThresholds from 'loyalty/selectors/getLoyaltyTierThresholds'
import moment from 'moment'

interface HotelTierProgressionEligibilityParams {
  statusCredits: number
  nights: number
  checkoutDate?: string
  skip?: boolean
}

type HotelTierProgressionEligibilityResult =
  | {
    showTierProgressionBanner: true
    /** The lowest tier that can be unlocked with this booking */
    lowestUnlockableTier: App.LoyaltyTier
    lowestUnlockableTierName: string
    /** The highest tier that can be reached with this booking */
    highestUnlockableTier: App.LoyaltyTier
    highestUnlockableTierName: string
    /** True if lowest !== highest (can skip tiers) */
    hasMultipleTierJumps: boolean
    isEligible: true
    isLoading: false
    statusReviewDate: string | undefined
  }
  | {
    showTierProgressionBanner: false
    lowestUnlockableTier: undefined
    lowestUnlockableTierName: undefined
    highestUnlockableTier: undefined
    highestUnlockableTierName: undefined
    hasMultipleTierJumps: false
    isEligible: boolean
    isLoading: boolean
    statusReviewDate: string | undefined
  }

function calculateHotelPointsEarnDate(checkoutDate: string | undefined): string | undefined {
  if (!checkoutDate) return undefined
  return moment(checkoutDate).add(1, 'day').format('YYYY-MM-DD')
}

function useHotelTierProgressionEligibility(params: HotelTierProgressionEligibilityParams): HotelTierProgressionEligibilityResult {
  const { statusCredits, nights, checkoutDate, skip = false } = params

  const loyaltyAccount = useAppSelector(state => state.loyalty.account.data)
  const loyaltyAccountLoading = useAppSelector(state => state.loyalty.account.fetching || state.loyalty.account.initial)
  const pendingPoints = useAppSelector(state => state.loyalty.accountPendingPoints.data)
  const tierThresholds = useAppSelector(getLoyaltyTierThresholds)

  const result = useMemo((): HotelTierProgressionEligibilityResult => {
    if (skip || !loyaltyAccount || loyaltyAccountLoading || !tierThresholds) {
      return {
        showTierProgressionBanner: false,
        lowestUnlockableTier: undefined,
        lowestUnlockableTierName: undefined,
        highestUnlockableTier: undefined,
        highestUnlockableTierName: undefined,
        hasMultipleTierJumps: false,
        isEligible: false,
        isLoading: !skip && (!!loyaltyAccountLoading || !tierThresholds),
        statusReviewDate: undefined,
      }
    }

    const pointsEarnDate = calculateHotelPointsEarnDate(checkoutDate)

    const eligibilityResult = calculateTierProgressionEligibility({
      currentTier: loyaltyAccount.tier,
      currentStatusCredits: loyaltyAccount.statusCredits ?? 0,
      currentNights: loyaltyAccount.numberOfNights ?? 0,
      statusCredits,
      nights,
      pointsEarnDate,
      statusReviewDate: loyaltyAccount.statusReviewDate,
      pendingPoints: pendingPoints ?? [],
      tierThresholds,
    })

    if (eligibilityResult.isEligible) {
      return {
        showTierProgressionBanner: true,
        lowestUnlockableTier: eligibilityResult.lowestUnlockableTier,
        lowestUnlockableTierName: eligibilityResult.lowestUnlockableTierName,
        highestUnlockableTier: eligibilityResult.highestUnlockableTier,
        highestUnlockableTierName: eligibilityResult.highestUnlockableTierName,
        hasMultipleTierJumps: eligibilityResult.hasMultipleTierJumps,
        isEligible: true,
        isLoading: false,
        statusReviewDate: loyaltyAccount.statusReviewDate,
      }
    }

    return {
      showTierProgressionBanner: false,
      lowestUnlockableTier: undefined,
      lowestUnlockableTierName: undefined,
      highestUnlockableTier: undefined,
      highestUnlockableTierName: undefined,
      hasMultipleTierJumps: false,
      isEligible: eligibilityResult.isEligible,
      isLoading: false,
      statusReviewDate: loyaltyAccount.statusReviewDate,
    }
  }, [
    skip,
    loyaltyAccount,
    loyaltyAccountLoading,
    statusCredits,
    nights,
    checkoutDate,
    pendingPoints,
    tierThresholds,
  ])

  return result
}

export default useHotelTierProgressionEligibility
